/**
 * Draws one piece of a plugin window ({@link UiNode}) by its type. The pieces themselves are in
 * `layout.tsx`, `text.tsx`, `table.tsx`, `fields.tsx` and `node.tsx`; they draw their children
 * through {@link UiContext.render}, which comes back here.
 */
import { Match, Switch, type JSX } from "solid-js";
import type { UiNode } from "@protocol/UiNode";
import type { UiContext } from "./context";
import { UiButton, UiCheckbox, UiInput, UiSelect } from "./fields";
import { UiColumn, UiDivider, UiList, UiRow } from "./layout";
import { UiNodeChip } from "./node";
import { UiTable } from "./table";
import { UiCode, UiHeading, UiText } from "./text";

/** `node` as the piece of type `T`, if it is one (lets each `Match` know the fields). */
function as<T extends UiNode["type"]>(node: UiNode, type: T): Extract<UiNode, { type: T }> | false {
  return node.type === type && (node as Extract<UiNode, { type: T }>);
}

/** One piece, drawn by its type. */
export function UiPart(props: { node: UiNode; ctx: UiContext }): JSX.Element {
  const n = () => props.node;
  return (
    <Switch fallback={null}>
      <Match when={as(n(), "column")}>{(p) => <UiColumn children={p().children} ctx={props.ctx} />}</Match>
      <Match when={as(n(), "row")}>{(p) => <UiRow children={p().children} ctx={props.ctx} />}</Match>
      <Match when={as(n(), "list")}>{(p) => <UiList items={p().items} ctx={props.ctx} />}</Match>
      <Match when={as(n(), "divider")}>
        <UiDivider />
      </Match>
      <Match when={as(n(), "heading")}>{(p) => <UiHeading text={p().text} />}</Match>
      <Match when={as(n(), "text")}>{(p) => <UiText text={p().text} tone={p().tone} />}</Match>
      <Match when={as(n(), "code")}>{(p) => <UiCode text={p().text} />}</Match>
      <Match when={as(n(), "table")}>{(p) => <UiTable columns={p().columns} rows={p().rows} ctx={props.ctx} />}</Match>
      <Match when={as(n(), "node")}>{(p) => <UiNodeChip id={p().id} label={p().label} ctx={props.ctx} />}</Match>
      <Match when={as(n(), "button")}>{(p) => <UiButton label={p().label} action={p().action} primary={p().primary} ctx={props.ctx} />}</Match>
      <Match when={as(n(), "input")}>{(p) => <UiInput name={p().name} label={p().label} placeholder={p().placeholder} ctx={props.ctx} />}</Match>
      <Match when={as(n(), "select")}>{(p) => <UiSelect name={p().name} label={p().label} options={p().options} ctx={props.ctx} />}</Match>
      <Match when={as(n(), "checkbox")}>{(p) => <UiCheckbox name={p().name} label={p().label} ctx={props.ctx} />}</Match>
    </Switch>
  );
}
